import { Dispatch, SetStateAction, useState } from 'react';

import Cross from '@common/Icons/Cross';
import Plus from '@common/Icons/Plus';
import { BodySmall } from '@common/body';
import { H5, H6 } from '@common/headline';
import { mixpanelTrackNext } from '@common/mixpanel';
import { ResizablePanel } from '../NhsSignup/ui';

type FAQsProps = {
  groupNames: string[];
  activeGroup: string;
  setActiveGroup: Dispatch<SetStateAction<string>>;
};

export const FAQsGroupSelector = ({
  groupNames,
  activeGroup,
  setActiveGroup,
}: FAQsProps): JSX.Element => {
  const activeStyle = 'bg-purple-200 text-blue';

  return (
    <div className="flex w-full flex-col items-center justify-center gap-y-2 bg-mono-white text-blue-400">
      {groupNames.map(group => (
        <a
          className={`flex w-full cursor-pointer items-center rounded-[30px] px-6 py-4 ${
            group === activeGroup && activeStyle
          }`}
          key={group}
          onClick={() => setActiveGroup(group)}
        >
          <H5>{group}</H5>
        </a>
      ))}
    </div>
  );
};

export const FAQsGroupScroll = ({
  groupNames,
  activeGroup,
  setActiveGroup,
}: FAQsProps): JSX.Element => {
  const activeStyle = 'border-b-2 border-blue text-blue pt-[2px]';

  return (
    <div className="flex h-16 w-full items-center justify-center">
      <div className="container flex h-full items-center justify-between gap-x-4 overflow-auto whitespace-nowrap px-4">
        {groupNames.map(group => (
          <div
            className={`flex h-full items-center ${
              group === activeGroup && activeStyle
            }`}
            key={group}
            onClick={() => setActiveGroup(group)}
          >
            <a>
              <H5>{group}</H5>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

const RoundIconVariants = {
  cross: <Cross height={15} strokeWidth={3} viewBox="0 0 24 24" width={15} />,
  plus: <Plus height={15} strokeWidth={3} viewBox="0 0 24 24" width={15} />,
};

type RoundIconProps = {
  variant: keyof typeof RoundIconVariants;
  iconStyles: string;
};

const RoundIcon = ({
  variant = 'plus',
  iconStyles,
}: RoundIconProps): JSX.Element => (
  <div
    className={`flex min-h-[32px] min-w-[32px] items-center justify-center rounded-full border-2 ${iconStyles}`}
  >
    {RoundIconVariants[variant]}
  </div>
);

export type FAQProps = {
  question: string;
  answer: string | JSX.Element;
};

type FAQPropsWithIconStyles = FAQProps & { iconStyles: string };

const FAQ = ({
  question,
  answer,
  iconStyles,
}: FAQPropsWithIconStyles): JSX.Element => {
  const [isPressed, setIsPressed] = useState(false);

  const onClick = () => {
    if (!isPressed) {
      mixpanelTrackNext('FAQ expanded', { faq: question });
    }

    setIsPressed(!isPressed);
  };

  return (
    <li className="mt-5 w-full first:mt-0">
      <div
        className="flex cursor-pointer items-center justify-between stroke-blue"
        onClick={onClick}
      >
        <div className="lg:hidden">
          <H5>{question}</H5>
        </div>
        <div className="hidden lg:block">
          <H6>{question}</H6>
        </div>
        <RoundIcon
          variant={isPressed ? 'cross' : 'plus'}
          iconStyles={iconStyles}
        />
      </div>
      <ResizablePanel>
        {isPressed && (
          <div className="mb-5 mt-4 w-full text-blue-800">
            <BodySmall>{answer}</BodySmall>
          </div>
        )}
      </ResizablePanel>
      <div className="mt-4 h-0 w-full rounded border-[1px] border-dawn-blue-200 bg-dawn-blue-200" />
    </li>
  );
};

export const FAQsList = ({
  FAQs,
  iconStyles = 'border-dawn-blue-50 bg-dawn-blue-150',
}: {
  FAQs: FAQProps[];
  iconStyles?: string;
}): JSX.Element => (
  <ul className="w-full">
    {FAQs.map(faq => (
      <FAQ
        answer={faq.answer}
        key={faq.question}
        question={faq.question}
        iconStyles={iconStyles}
      />
    ))}
  </ul>
);
